/**
 * Métadonnées IHM pour les résultats de recherche globale (lot 6).
 *
 * Aligné sur l’enum API `search_entity_type_enum` / ESearchEntityType.
 */
export type SearchEntityType =
  | 'agent'
  | 'direction'
  | 'domaine'
  | 'agence'
  | 'equipe'
  | 'section'
  | 'site'
  | 'team_contact'
  | 'tag'

/** Ordre d’affichage par défaut des groupes de résultats (plus petit = plus prioritaire). */
export const SEARCH_ENTITY_DISPLAY_ORDER: Record<SearchEntityType, number> = {
  agent: 10,
  team_contact: 20,
  equipe: 30,
  direction: 40,
  domaine: 50,
  agence: 60,
  section: 70,
  site: 80,
  tag: 90,
}

/** Libellés français des types d’entité (alignés backend). */
export const SEARCH_ENTITY_TYPE_LABEL: Record<SearchEntityType, string> = {
  agent: 'Agent',
  direction: 'Direction',
  domaine: 'Domaine',
  agence: 'Agence',
  equipe: 'Équipe',
  section: 'Section',
  site: 'Site',
  team_contact: 'Contact équipe',
  tag: 'Tag',
}

/** Icônes Material Symbols par type d’entité (non fournies par l’API). */
export const SEARCH_ENTITY_ICON: Record<SearchEntityType, string> = {
  agent: 'person',
  direction: 'corporate_fare',
  domaine: 'folder',
  agence: 'location_city',
  equipe: 'groups',
  section: 'account_tree',
  site: 'location_on',
  team_contact: 'contact_phone',
  tag: 'sell',
}

/** Libellés FR des champs matchés renvoyés par l’API. */
const SEARCH_MATCHED_FIELD_LABEL: Record<string, string> = {
  nni: 'NNI',
  identity: 'nom',
  function: 'fonction',
  team: 'équipe',
  direction: 'direction',
  domain: 'domaine',
  agency: 'agence',
  section: 'section',
  site: 'site',
  tag: 'tag',
  contact: 'contact',
  contactField: 'champ libre',
  description: 'description',
  title: 'titre',
  subtitle: 'sous-titre',
  primary: 'champ principal',
}

/** Tous les types d’entité, triés par ordre d’affichage. */
export const SEARCH_ENTITY_TYPES_ORDERED: SearchEntityType[] = (
  Object.keys(SEARCH_ENTITY_DISPLAY_ORDER) as SearchEntityType[]
).sort(function compareEntityOrder(leftType, rightType) {
  return SEARCH_ENTITY_DISPLAY_ORDER[leftType] - SEARCH_ENTITY_DISPLAY_ORDER[rightType]
})

/**
 * Traduit et déduplique les champs matchés pour l’affichage.
 *
 * @param matchedFields - Identifiants techniques renvoyés par l’API.
 * @returns Libellés FR uniques, dans l’ordre d’apparition.
 */
export function formatSearchMatchedFields(matchedFields: string[]): string[] {
  const seenLabels = new Set<string>()
  const formattedLabels: string[] = []

  for (const matchedField of matchedFields) {
    const label = SEARCH_MATCHED_FIELD_LABEL[matchedField] ?? matchedField
    if (seenLabels.has(label)) {
      continue
    }
    seenLabels.add(label)
    formattedLabels.push(label)
  }

  return formattedLabels
}
